import useLogsController from '@/hooks/useLogsController';
import DatePicker from '@/shared/components/ui/DatePicker';
import { Dropdown, DropdownItem } from '@/shared/components/ui/Dropdown';
import { motion } from 'framer-motion';
import { Loader, Search, ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ReactPaginate from 'react-paginate';

const Activities = () => {
    const { t } = useTranslation();
    const {
        logs, setDate, handleQuickSelect, isLoading, date,
        selectedAction, setSelectedAction, page, setPage, totalPages, pageSize,
        searchValue, setSearchValue, totalLogsCount, handleExport,
        isSearching
    } = useLogsController();

    const renderNote = (note: string) => {
        if (!note) return '---';
        if (note.startsWith('EXCHANGE_ORDER:')) {
            const match = note.match(/#(\d+)/);
            const orderId = match ? match[1] : '';

            return t('exchange_order_note', { orderId });
        }
        return t(note) || note;
    };

    return (
        <>
            {/* <Header title={t('activities_log')} /> */}

            {/* <main className="px-4 py-4 min-h-[calc(100vh-100px)] flex flex-col items-center justify-center"> */}
                <div className="w-full max-w-7xl">

                    <div className="mb-6 w-full max-w-7xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
                        <div className="flex flex-col gap-1.5 w-full">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-slate-500">{t("start_date")}</label>
                                <Dropdown label={t("quick_select")} >
                                    <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                </Dropdown>
                            </div>
                            <DatePicker
                                enableTime
                                id="pp-from"
                                label=""
                                placeholder={t("Select a date")}
                                onChange={(_, d) => setDate(p => ({ ...p, from: d }))} defaultDate={date.from} />
                        </div>

                        <div className="flex flex-col gap-1.5 w-full">
                            <div className="flex items-center justify-between">
                                <label className="text-xs font-semibold text-slate-500">{t("end_date")}</label>
                                <Dropdown label={t("quick_select")}>
                                    <DropdownItem onClick={() => handleQuickSelect('today')}>{t("today")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('yesterday')}>{t("yesterday")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('current_month')}>{t("current month")}</DropdownItem>
                                    <DropdownItem onClick={() => handleQuickSelect('last_month')}>{t("before month")}</DropdownItem>
                                </Dropdown>
                            </div>
                            <DatePicker
                                enableTime
                                id="pp-to"
                                label=""
                                placeholder={t("Select a date")}
                                onChange={(_, d) => setDate(p => ({ ...p, to: d }))} defaultDate={date.to} />
                        </div>

                        <div className="flex flex-col gap-1.5 w-full">
                            <label className="text-xs font-semibold text-slate-500">{t("action_type")}</label>
                            <div className="relative w-full">
                                <select
                                    name="action"
                                    value={selectedAction}
                                    onChange={(e) => setSelectedAction(e.target.value)}
                                    className="w-full appearance-none bg-white border border-slate-200 rounded-xl px-4 py-2.5 pr-10 text-sm text-slate-900 font-medium shadow-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-400 outline-none cursor-pointer transition-all hover:border-slate-300"
                                >
                                    <option value="ALL">{t("all_actions")}</option>
                                    <option value="CREATE">{t("action_create")}</option>
                                    <option value="UPDATE">{t("action_update")}</option>
                                    <option value="DELETE">{t("action_delete")}</option>
                                    <option value="PREPARE_PIECE">{t("action_prepare_piece")}</option>
                                    <option value="FORWARD_OPTIMUS">{t("action_forward_optimus")}</option>
                                    <option value="FORWARD_VELOX">{t("action_forward_velox")}</option>
                                    <option value="FORWARD_EXTERNAL">{t("action_forward_external")}</option>
                                </select>
                                <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                    </svg>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5 w-full">
                            <label className="text-xs font-semibold text-slate-500">{t("search")}</label>
                            <div className="relative w-full">
                                <input
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    type="text"
                                    placeholder={t("search_by_name_phone_or_id")}
                                    className="w-full bg-white border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-400 shadow-sm transition-all hover:border-slate-300"
                                />
                                <div className="pointer-events-none absolute inset-y-0 left-3 flex items-center">
                                    {isSearching ? (
                                        <Loader className="w-4 h-4 text-emerald-500 animate-spin" />
                                    ) : (
                                        <Search className="w-4 h-4 text-slate-400" />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mb-4 px-2">
                        <div className="flex items-center gap-2 text-sm bg-white border border-slate-200 shadow-sm px-4 py-2 rounded-xl font-semibold text-slate-700">
                            <ShoppingBag className="w-4 h-4 text-violet-500" />
                            <span>{t("total_activities")}:</span>
                            <span className="text-violet-600 font-bold">{totalLogsCount}</span>
                        </div>
                        <div className='flex flex-col gap-2.5'>
                            <div className="flex items-center gap-2 ">
                                <button
                                    onClick={() => handleExport('pdf', 'تقرير سجل الأنشطة ')}
                                    className="bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                                >

                                    تصدير PDF
                                </button>
                                <button
                                    onClick={() => handleExport('excel', 'تقرير سجل الأنشطة ')}
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition-colors"
                                >

                                    تصدير Excel
                                </button>
                            </div>
                        </div>

                    </div>

                    {isLoading ? (
                        <div className="flex justify-center items-center py-12">
                            <Loader className="w-8 h-8 text-violet-600 animate-spin" />
                        </div>
                    ) : (
                        <div className="bg-white rounded-[1rem] border border-slate-200 shadow-xl shadow-slate-200/50 overflow-hidden">
                            <div className={`overflow-x-auto transition-opacity duration-200 ${isSearching ? 'opacity-50' : 'opacity-100'}`}>
                                <table className="w-full text-right border-collapse">
                                    <thead>
                                        <tr className="text-slate-800 uppercase font-black tracking-widest border-b border-slate-100 bg-slate-50/30">
                                            <th className="px-6 py-4">{t('log_id')}</th>
                                            <th className="px-6 py-4">{t('user')}</th>
                                            <th className="px-6 py-4">{t('action')}</th>
                                            <th className="px-6 py-4">{t('operation')}</th>
                                            <th className="px-6 py-4">{t('description')}</th>
                                            {/* <th className="px-6 py-4">{t('type')}</th> */}
                                            <th className="px-6 py-4">{t('date_time')}</th>
                                            <th className="px-6 py-4">{t('note')}</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50">
                                        {logs?.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className="px-6 py-12 text-center text-slate-900 text-sm italic">
                                                    {t('no_logs_found')}
                                                </td>
                                            </tr>
                                        ) : (
                                            logs?.map((log: any, index: number) => (
                                                <motion.tr
                                                    key={log.id}
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: index * 0.02 }}
                                                    className="hover:bg-slate-50/50 transition-colors"
                                                >
                                                    <td className="px-6 py-4 text-sm font-medium text-violet-600">
                                                        #{log.id}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-900 font-medium">
                                                        {log.mac || '---'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm">
                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium`}>
                                                            {t(log.action)}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-600">
                                                        {t(log.opname) || '---'}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs">
                                                        <span title={log.title}>
                                                            {log.title || '---'}
                                                        </span>
                                                    </td>
                                                    {/* <td className="px-6 py-4 text-sm text-slate-500">
                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                                {log.ltype}
                                                            </span>
                                                        </td> */}
                                                    <td className="px-6 py-4 text-sm text-slate-500 whitespace-nowrap">
                                                        {log.tdate}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-slate-600 max-w-xs">
                                                        {renderNote(log.lnote)}
                                                    </td>
                                                </motion.tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {totalPages > 1 && (
                                <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/30">
                                    <span className="text-xs text-slate-500">
                                        {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalPages)} {t("of")} {totalPages}
                                    </span>
                                    <ReactPaginate
                                        pageCount={totalPages}
                                        forcePage={page}
                                        onPageChange={({ selected }) => setPage(selected)}
                                        marginPagesDisplayed={1}
                                        pageRangeDisplayed={3}
                                        previousLabel="‹"
                                        nextLabel="›"
                                        breakLabel="…"
                                        containerClassName="flex items-center gap-1"
                                        pageClassName="inline-flex cursor-pointer"
                                        pageLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                        activeClassName="!text-white"
                                        activeLinkClassName="!bg-violet-600 !text-white shadow-sm"
                                        previousClassName="inline-flex"
                                        previousLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                        nextClassName="inline-flex"
                                        nextLinkClassName="cursor-pointer min-w-[2rem] h-8 px-2 flex items-center justify-center rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-200 transition-colors"
                                        breakClassName="inline-flex"
                                        breakLinkClassName="min-w-[2rem] h-8 px-2 flex items-center justify-center text-slate-400 text-sm select-none"
                                        disabledClassName="opacity-30 cursor-not-allowed pointer-events-none"
                                        renderOnZeroPageCount={null}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </div>
            {/* </main> */}
        </>
    );
};

export default Activities;